/* Homepage blog section with square 1:1 image frames, cream canvas, dark-green typography, and directional card shadows.
   Updated: Added DataFast blog_read_more goal when a blog card is clicked. */

import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { getBlogImageUrl } from '@/lib/image-url';
import { trackGoal } from '@/lib/datafast';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  image_url: string;
  image_storage_path?: string;
  slug: string;
}

const Blogs = () => {
  const [blogPosts, setBlogPosts] = useState<Array<BlogPost & { imagePosition: 'left' | 'right' }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const now = new Date().toISOString();
        const { data, error } = await supabase
          .from('blogs')
          .select('id, title, excerpt, image_url, image_storage_path, slug')
          .eq('published', true)
          .or(`scheduled_at.is.null,scheduled_at.lte.${now}`)
          .order('published_at', { ascending: false })
          .limit(2);

        if (error) throw error;

        if (data) {
          const postsWithPosition = data.map((post: BlogPost, index: number) => ({
            ...post,
            imagePosition: (index % 2 === 0 ? 'left' : 'right') as 'left' | 'right'
          }));
          setBlogPosts(postsWithPosition);
        }
      } catch (error) {
        // Error fetching blogs
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  return (
    <section id="blogs" data-fast-scroll="viewed_blogs_section" className="relative isolate overflow-hidden bg-[#fcfaf6] py-20 text-[#1e2b22] sm:py-24 lg:py-28">
      {/* subtle radial glow for depth */}
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(100%_60%_at_50%_0%,rgba(137,146,127,0.08),transparent_60%)]"
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-[1240px] px-5 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
          className="mb-10 flex items-end justify-between gap-6"
        >
          <div>
            <p className="mb-3 text-xs uppercase tracking-[0.18em] text-[#5a6552]">
              Ontdek alles over de planten die wij verkopen
            </p>
            <h2 className="text-4xl tracking-[-0.035em] text-[#1e2b22] md:text-5xl">
              Blogs
            </h2>
            <p className="mt-3 max-w-xl text-base leading-7 text-[#5a6552] sm:text-lg sm:leading-8">
              We helpen je graag een handje bij het kiezen van de juiste haagplant, het onderhouden van jouw haagplanten en alles wat daarbij komt kijken.
            </p>
          </div>
          <a href="/blogs" className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#1e2b22] hover:text-[#5a6552] transition-colors">
            Bekijk alle blogs <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </a>
        </motion.div>

        {/* Blog Posts */}
        <div className="space-y-8 lg:space-y-10">
          {loading ? (
            <div className="py-12 text-center text-[#5a6552]">Blogs laden...</div>
          ) : blogPosts.length === 0 ? (
            <div className="py-12 text-center text-[#5a6552]">Nóg geen blogs beschikbaar</div>
          ) : (
            blogPosts.map((post, index) => (
              <Link key={post.id} to={`/blogs/${post.slug}`} onClick={() => trackGoal('blog_read_more', { title: post.title })} className="group block">
                <motion.article
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.75, delay: index * 0.15, ease: [0.22, 1, 0.36, 1] }}
                  viewport={{ once: true }}
                  className={`relative overflow-hidden rounded-xl bg-white ring-1 ring-[#e7e1d6] transition-[transform,box-shadow] duration-300 group-hover:-translate-y-1 lg:flex lg:items-stretch lg:gap-0 ${
                    post.imagePosition === 'right'
                      ? 'lg:flex-row-reverse group-hover:shadow-[0_14px_30px_rgba(30,43,34,0.12)]'
                      : 'group-hover:shadow-[14px_14px_30px_-10px_rgba(30,43,34,0.16)]'
                  }`}
                >
                  {/* Image side */}
                  <div className="relative aspect-square w-full overflow-hidden lg:w-1/2 lg:flex-shrink-0">
                    <img
                      src={
                        post.image_storage_path
                          ? getBlogImageUrl(post.image_storage_path, 'medium')
                          : post.image_url || '/assets/sections/blogs.png'
                      }
                      alt={post.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    {/* dark gradient overlay on image */}
                    <div
                      className={`pointer-events-none absolute inset-0 bg-[linear-gradient(0deg,rgba(11,18,13,0.2)_0%,transparent_50%)] ${
                        post.imagePosition === 'right'
                          ? 'lg:bg-[linear-gradient(270deg,rgba(11,18,13,0.2)_0%,transparent_50%)]'
                          : 'lg:bg-[linear-gradient(90deg,rgba(11,18,13,0.2)_0%,transparent_50%)]'
                      }`}
                      aria-hidden="true"
                    />
                  </div>

                  {/* Text side */}
                  <div className="flex flex-col justify-center p-6 sm:p-8 lg:w-1/2 lg:p-10 xl:p-12">
                    <h3 className="text-2xl font-semibold leading-tight tracking-[-0.02em] text-[#1e2b22] xl:text-3xl">
                      {post.title}
                    </h3>
                    <p className="mt-4 text-sm leading-7 text-[#5a6552] sm:text-base sm:leading-7">
                      {post.excerpt}
                    </p>
                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#1e2b22]">
                      Lees meer
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                    </span>
                  </div>
                </motion.article>
              </Link>
            ))
          )}
        </div>


      </div>
    </section>
  );
};

export default Blogs;
